import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  InputGroup,
  InputRightElement,
  Badge,
  Divider,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  Progress,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  IconButton,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  List,
  ListItem,
  ListIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import { 
  FiDollarSign, 
  FiArrowRight, 
  FiCopy, 
  FiCheck, 
  FiAlertTriangle, 
  FiInfo, 
  FiHelpCircle,
  FiArrowLeft,
  FiMaximize2,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiClock,
  FiCheckCircle,
} from 'react-icons/fi';
import { useAccount, useBalance, useContractRead, useContractWrite, useWaitForTransaction } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { useTranslation } from 'react-i18next';
import ConnectButton from '@/components/common/ConnectButton';
import PageHeader from '@/components/common/PageHeader';
import { useDORContract } from '@/hooks/useDORContract';
import { NETWORKS } from '@/config/networks';
import { ethers } from 'ethers';

import PriceChart from '@/components/common/PriceChart';
import { formatDOR, formatUSD, formatNumberWithCommas } from '@/utils/formatters';

// 预览数据类型定义
interface PreviewData {
  circulatingDOR: number;
  lockedDOR: number;
  totalDOR: number;
  estimatedValue: number;
  fee: number;
  gasEstimate: number;
  isCurrentBalance: boolean;
  newDOR: number;
  newCirculatingDOR: number; // 新增流通DOR
  newLockedDOR: number; // 新增锁定DOR
}


const Recharge: React.FC = () => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('recharge');
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 使用DOR合约Hook
  const {
    contract,
    tokenInfo,
    userBalance,
    userCirculatingBalance,
    userLockInfo,
    stats,
    loading: contractLoading,
    error: contractError,
    recharge,
    rechargeWithInviter,
    approveUSDT,
  } = useDORContract();

  // 获取USDT余额
  const { data: usdtBalance } = useBalance({
    address: address,
    token: NETWORKS[97]?.contracts.usdtToken as `0x${string}`,
    watch: true,
  });

  // 状态管理
  const [amount, setAmount] = useState('');
  const [inviterAddress, setInviterAddress] = useState('');
  const [isApproved, setIsApproved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [inviterAddressError, setInviterAddressError] = useState('');
  const [amountError, setAmountError] = useState('');

  // 从合约获取数据
  const currentPrice = stats?.price ? parseFloat(stats.price) : 1.0;
  const totalSupply = stats?.totalSupply ? parseFloat(stats.totalSupply) : 0;
  const totalLocked = stats?.totalLocked ? parseFloat(stats.totalLocked) : 0;
  const userLockedAmount = userLockInfo?.lockedAmount ? parseFloat(userLockInfo.lockedAmount) : 0;
  const userCirculatingAmount = userCirculatingBalance ? parseFloat(userCirculatingBalance) : 0;
  
  // 配置参数
  const minAmount = 1;
  const maxAmount = 10000;
  const unlockRatio = 2.0; // 解锁比例：1 USDT = 2 DOR (1流通 + 1锁定)

  // 生成基于当前价格的24小时价格趋势数据
  const generatePriceData = () => {
    const data = [];
    const basePrice = currentPrice;
    const volatility = 0.02; // 2%波动率
    
    for (let i = 0; i < 24; i += 2) {
      const time = `${i.toString().padStart(2, '0')}:00`;
      // 生成基于当前价格的随机波动
      const randomChange = (Math.random() - 0.5) * volatility;
      const price = basePrice * (1 + randomChange);
      data.push({ time, price: parseFloat(price.toFixed(3)) });
    }
    
    return data;
  };

  const mockPriceData = generatePriceData();

  // 计算价格趋势统计
  const priceStats = useMemo(() => {
    if (mockPriceData.length === 0) return null;
    
    const prices = mockPriceData.map(d => d.price);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const priceChange = ((currentPrice - prices[0]) / prices[0]) * 100;
    
    return {
      maxPrice,
      minPrice,
      avgPrice,
      priceChange,
      volatility: ((maxPrice - minPrice) / avgPrice) * 100
    };
  }, [mockPriceData, currentPrice]);

  // 计算预览数据
  const previewData: PreviewData | null = useMemo(() => {
    const currentUserBalance = userBalance ? parseFloat(userBalance) : 0;
    
    if (!amount || parseFloat(amount) === 0) {
      // 如果没有输入金额，显示当前余额
      return {
        circulatingDOR: userCirculatingAmount, // 流通DOR = 总余额 - 锁定余额
        lockedDOR: userLockedAmount, // 从合约获取锁定余额
        totalDOR: currentUserBalance,
        estimatedValue: userCirculatingAmount * currentPrice, // 只计算流通DOR的价值
        fee: 0,
        gasEstimate: 0,
        isCurrentBalance: true, // 标记这是当前余额
        newDOR: 0, // 新增的DOR数量为0
        newCirculatingDOR: 0,
        newLockedDOR: 0,
      };
    }
    
    const inputAmount = parseFloat(amount);
    // 1:1 兑换比例：1 USDT = 1流通DOR + 1锁定DOR = 2 DOR
    const newCirculatingDOR = inputAmount; // 1 USDT = 1 流通DOR
    const newLockedDOR = inputAmount; // 1 USDT = 1 锁定DOR
    const totalNewDOR = newCirculatingDOR + newLockedDOR; // 总共2 DOR
    
    // 计算充值后的总余额
    const totalCirculatingDOR = userCirculatingAmount + newCirculatingDOR; // 当前流通 + 新流通
    const totalLockedDOR = userLockedAmount + newLockedDOR; // 当前锁定 + 新锁定
    const totalDOR = totalCirculatingDOR + totalLockedDOR;
    const estimatedValue = totalCirculatingDOR * currentPrice; // 只计算流通DOR的价值
    
    return {
      circulatingDOR: totalCirculatingDOR,
      lockedDOR: totalLockedDOR,
      totalDOR,
      estimatedValue,
      fee: 0, // 不再计算手续费
      gasEstimate: 0, // 不再计算gas估算
      isCurrentBalance: false, // 标记这是充值后的余额
      newDOR: totalNewDOR, // 新增的DOR数量
      newCirculatingDOR: newCirculatingDOR,
      newLockedDOR: newLockedDOR,
    };
  }, [amount, currentPrice, userBalance, userLockedAmount, userCirculatingAmount, userCirculatingBalance]);

  // 处理金额输入
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 只允许正整数
    if (value === '' || /^\d+$/.test(value)) {
      let num = parseInt(value);
      const max = usdtBalance ? Math.floor(parseFloat(usdtBalance.formatted)) : 0;
      
      if (value === '') {
        setAmount('');
        setAmountError('');
      } else if (num < minAmount) {
        setAmount(value);
        setAmountError(`最少需要 ${minAmount} USDT`);
      } else if (num > max) {
        setAmount(max > 0 ? max.toString() : '');
        setAmountError('输入金额不能超过USDT余额');
      } else {
        setAmount(value);
        setAmountError('');
      }
    }
  };

  // 设置最大金额
  const setMaxAmount = () => {
    // 使用用户的实际USDT余额（取整数）
    if (usdtBalance && parseFloat(usdtBalance.formatted) > 0) {
      const maxAmount = Math.floor(parseFloat(usdtBalance.formatted));
      setAmount(maxAmount > 0 ? maxAmount.toString() : '1');
    } else {
      // 默认设置一个合理的金额
      setAmount('100');
    }
  };

  // 验证邀请人地址格式
  const validateInviterAddress = (inputAddress: string): boolean => {
    if (!inputAddress.trim()) return true; // 空值视为有效（可选）
    
    // 检查是否为有效的以太坊地址格式
    const addressRegex = /^0x[a-fA-F0-9]{40}$/;
    if (!addressRegex.test(inputAddress.trim())) {
      setInviterAddressError('请输入有效的钱包地址');
      return false;
    }
    
    // 检查是否为自己的地址
    if (inputAddress && inputAddress.trim().toLowerCase() === address?.toLowerCase()) {
      setInviterAddressError('不能使用自己的地址作为邀请人');
      return false;
    }
    
    setInviterAddressError('');
    return true;
  };

  // 处理邀请人地址输入变化
  const handleInviterAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInviterAddress(value);
    validateInviterAddress(value);
  };

  // 批准USDT
  const handleApprove = async () => {
    if (!amount || parseInt(amount) < minAmount) {
      toast({
        title: t('errors.inputError'),
        description: `${t('errors.minAmountError')} ${minAmount} USDT`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!address) {
      toast({
        title: t('errors.approveFailed'),
        description: '请先连接钱包',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      await approveUSDT(amount);
      setIsApproved(true);
      setCurrentStep(2);
      
      toast({
        title: t('errors.approveSuccess'),
        description: t('errors.approveSuccessDesc'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('USDT批准失败:', error);
      toast({
        title: t('errors.approveFailed'),
        description: error?.message || t('errors.approveFailedDesc'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 确认充值
  const handleRecharge = async () => {
    if (!riskAccepted) {
      toast({
        title: t('errors.confirmRisk'),
        description: t('errors.confirmRiskDesc'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 根据价格状态显示额外警告
    if (currentPrice < 0.95) {
      toast({
        title: t('errors.severePriceRisk'),
        description: t('errors.severePriceRiskDesc'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (currentPrice < 0.99) {
      toast({
        title: t('errors.priceReminder'),
        description: t('errors.priceReminderDesc'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }

    onOpen(); // 打开确认模态
  };

  // 执行充值
  const executeRecharge = async () => {
    if (!contract || !amount) {
      toast({
        title: t('exchangeFailed'),
        description: '合约未初始化或金额无效',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!address) {
      toast({
        title: t('exchangeFailed'),
        description: '请先连接钱包',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      // 显示交易提交提示
      toast({
        title: '交易提交中',
        description: '正在提交充值交易，请等待确认...',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // 调用合约充值功能
      let tx;
      if (inviterAddress && inviterAddress.trim()) {
        // 如果有邀请人地址，使用带邀请人的充值方法
        tx = await rechargeWithInviter(amount, inviterAddress.trim());
      } else {
        // 否则使用普通充值方法
        tx = await recharge(amount);
      }
      
      // 显示交易确认提示
      toast({
        title: '交易确认中',
        description: `交易哈希: ${tx.hash.slice(0, 10)}...${tx.hash.slice(-8)}`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      // 等待交易确认
      const receipt = await tx.wait();
      
      // 检查交易状态
      if (receipt.status === 1) {
        toast({
          title: t('exchangeSuccess'),
          description: `${t('errors.exchangeSuccessDesc')} ${amount} USDT，${t('errors.exchangeSuccessDesc2')} ${previewData?.newDOR.toFixed(2)} DOR (${previewData?.newCirculatingDOR.toFixed(2)} 流通 + ${previewData?.newLockedDOR.toFixed(2)} 锁定)`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // 重置表单
        setAmount('');
        setInviterAddress('');
        setIsApproved(false);
        setRiskAccepted(false);
        setCurrentStep(1);
        
        // 跳转到交易历史
        navigate('/history');
      } else {
        throw new Error('交易执行失败');
      }
    } catch (error: any) {
      console.error('充值失败:', error);
      
      // 根据错误类型显示不同的错误信息
      let errorMessage = t('errors.exchangeFailedDesc');
      
      if (error.code === 'ACTION_REJECTED') {
        errorMessage = '用户取消了交易';
      } else if (error.code === 'INSUFFICIENT_FUNDS') {
        errorMessage = '余额不足，请检查USDT余额';
      } else if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
        errorMessage = 'Gas估算失败，请稍后重试';
      } else if (error.message?.includes('insufficient allowance')) {
        errorMessage = 'USDT批准额度不足，请重新批准';
      } else if (error.message?.includes('insufficient balance')) {
        errorMessage = 'USDT余额不足';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: t('exchangeFailed'),
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  // 复制邀请码
  const copyInviteCode = () => {
    navigator.clipboard.writeText('DOR123456');
    setCopied(true);
    toast({
      title: t('inviteCode.copySuccess'),
      description: t('inviteCode.copyDescription'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  // 如果未连接钱包，显示连接提示
  if (!isConnected) {
    return (
      <Box>
        <VStack spacing={{ base: 4, md: 6 }} align="stretch">
          {/* 顶部区域 */}
          <PageHeader title={t('title')} />

          {/* 价格趋势图表 - 移动端隐藏 */}
          <Box display={{ base: "none", lg: "block" }}>
            <PriceChart 
              data={mockPriceData}
              isLoading={false}
              t={t}
            />
            
            {/* 价格统计信息 */}
            {priceStats && (
              <Card bg={cardBg} border="1px" borderColor={borderColor} mt={4}>
                <CardBody>
                  <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
                    <Stat>
                      <StatLabel fontSize="sm" color="gray.500">{t('priceStats.highest')}</StatLabel>
                      <StatNumber fontSize="lg" color="green.500">
                        {priceStats.maxPrice.toFixed(3)} USDT
                      </StatNumber>
                    </Stat>
                    
                    <Stat>
                      <StatLabel fontSize="sm" color="gray.500">{t('priceStats.lowest')}</StatLabel>
                      <StatNumber fontSize="lg" color="red.500">
                        {priceStats.minPrice.toFixed(3)} USDT
                      </StatNumber>
                    </Stat>
                    
                    <Stat>
                      <StatLabel fontSize="sm" color="gray.500">{t('priceStats.average')}</StatLabel>
                      <StatNumber fontSize="lg">
                        {priceStats.avgPrice.toFixed(3)} USDT
                      </StatNumber>
                    </Stat>
                    
                    <Stat>
                      <StatLabel fontSize="sm" color="gray.500">{t('priceStats.change24h')}</StatLabel>
                      <StatNumber fontSize="lg" color={priceStats.priceChange >= 0 ? 'green.500' : 'red.500'}>
                        <StatArrow type={priceStats.priceChange >= 0 ? 'increase' : 'decrease'} />
                        {Math.abs(priceStats.priceChange).toFixed(2)}%
                      </StatNumber>
                    </Stat>
                  </Grid>
                  
                  <Divider my={4} />
                  
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">{t('priceStats.volatility')}</Text>
                      <Text fontSize="md" fontWeight="bold">
                        {priceStats.volatility.toFixed(2)}%
                      </Text>
                    </VStack>
                    
                    <Badge 
                      colorScheme={priceStats.volatility < 2 ? 'green' : priceStats.volatility < 5 ? 'yellow' : 'red'}
                      fontSize="sm"
                      px={3}
                      py={1}
                    >
                      {priceStats.volatility < 2 ? t('priceStats.lowVolatility') : 
                       priceStats.volatility < 5 ? t('priceStats.mediumVolatility') : 
                       t('priceStats.highVolatility')}
                    </Badge>
                  </HStack>
                </CardBody>
              </Card>
            )}
          </Box>
          
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody textAlign="center" py={{ base: 8, md: 12 }}>
              <Box mb={4}><FiDollarSign size={64} color="var(--chakra-colors-green-500)" /></Box>
              <Heading size={{ base: "sm", md: "md" }} mb={2}>
                {t('connectWalletPrompt')}
              </Heading>
              <Text color="gray.500" mb={6} fontSize={{ base: "sm", md: "md" }}>
                {t('connectWalletDescription')}
              </Text>
              <ConnectButton />
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* 顶部区域 */}
        <PageHeader title={t('title')} />

        {/* 合约错误提示 */}
        {contractError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>合约连接错误</AlertTitle>
              <AlertDescription>
                {contractError}
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* 价格趋势图表 - 移动端隐藏 */}
        <Box display={{ base: "none", lg: "block" }}>
          <PriceChart 
            data={mockPriceData}
            isLoading={contractLoading}
            t={t}
          />
          
          {/* 价格统计信息 */}
          {priceStats && (
            <Card bg={cardBg} border="1px" borderColor={borderColor} mt={4}>
              <CardBody>
                <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
                  <Stat>
                    <StatLabel fontSize="sm" color="gray.500">{t('priceStats.highest')}</StatLabel>
                    <StatNumber fontSize="lg" color="green.500">
                      {priceStats.maxPrice.toFixed(3)} USDT
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="sm" color="gray.500">{t('priceStats.lowest')}</StatLabel>
                    <StatNumber fontSize="lg" color="red.500">
                      {priceStats.minPrice.toFixed(3)} USDT
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="sm" color="gray.500">{t('priceStats.average')}</StatLabel>
                    <StatNumber fontSize="lg">
                      {priceStats.avgPrice.toFixed(3)} USDT
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="sm" color="gray.500">{t('priceStats.change24h')}</StatLabel>
                    <StatNumber fontSize="lg" color={priceStats.priceChange >= 0 ? 'green.500' : 'red.500'}>
                      <StatArrow type={priceStats.priceChange >= 0 ? 'increase' : 'decrease'} />
                      {Math.abs(priceStats.priceChange).toFixed(2)}%
                    </StatNumber>
                  </Stat>
                </Grid>
                
                <Divider my={4} />
                
                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">{t('priceStats.volatility')}</Text>
                    <Text fontSize="md" fontWeight="bold">
                      {priceStats.volatility.toFixed(2)}%
                    </Text>
                  </VStack>
                  
                  <Badge 
                    colorScheme={priceStats.volatility < 2 ? 'green' : priceStats.volatility < 5 ? 'yellow' : 'red'}
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {priceStats.volatility < 2 ? t('priceStats.lowVolatility') : 
                     priceStats.volatility < 5 ? t('priceStats.mediumVolatility') : 
                     t('priceStats.highVolatility')}
                  </Badge>
                </HStack>
              </CardBody>
            </Card>
          )}
        </Box>

        {/* 交易进度 */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4}>
              <HStack justify="space-between" w="full">
                <Text fontWeight="bold">{t('transactionProgress')}</Text>
                <Text color="gray.500">{t('step')} {currentStep}/2</Text>
              </HStack>
              <Progress 
                value={currentStep * 50} 
                colorScheme="blue" 
                size="lg"
                borderRadius="full"
              />
              <HStack justify="space-between" w="full" fontSize="sm">
                <Text color={currentStep >= 1 ? 'blue.500' : 'gray.400'}>
                  {t('approveUSDT')}
                </Text>
                <Text color={currentStep >= 2 ? 'blue.500' : 'gray.400'}>
                  {t('confirmExchange')}
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* 主体内容区 - 电脑端并排显示，移动端垂直堆叠 */}
        <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={{ base: 4, md: 6 }}>
          {/* 输入表单模块 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">{t('inputAmount')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6}>
                {/* USDT余额显示 */}
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>{t('bscTestnetBalance')}</AlertTitle>
                    <AlertDescription>
                      {usdtBalance ? `${parseFloat(usdtBalance.formatted).toFixed(2)} USDT` : '加载中...'} (BSC测试网)
                    </AlertDescription>
                  </Box>
                </Alert>

                {/* 充值金额输入 */}
                <FormControl isInvalid={!!amountError}>
                  <FormLabel>兑换金额 (USDT)</FormLabel>
                  <InputGroup size="lg">
                    <Input
                      type="number"
                      min={minAmount}
                      step="1"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder="输入整数，最少1 USDT"
                    />
                    <InputRightElement width="8.5rem">
                      <Text color="gray.500" fontSize="sm" cursor="pointer" onClick={setMaxAmount}>
                        USDT余额: {usdtBalance ? Math.floor(parseFloat(usdtBalance.formatted)) : '--'}
                      </Text>
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText color={amountError ? 'red.500' : undefined}>
                    {amountError || `最少 ${minAmount} USDT，只能输入整数`}
                  </FormHelperText>
                </FormControl>

                {/* 邀请人地址输入 */}
                <FormControl isInvalid={!!inviterAddressError}>
                  <FormLabel>邀请人地址（可选）</FormLabel>
                  <Input
                    value={inviterAddress}
                    onChange={handleInviterAddressChange}
                    placeholder="0x..."
                    size="lg"
                  />
                  <FormHelperText>
                    {inviterAddressError || "输入邀请人的钱包地址，可获得额外奖励"}
                  </FormHelperText>
                </FormControl>

                {/* 批准按钮 */}
                <Button
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  onClick={handleApprove}
                  isLoading={isLoading && currentStep === 1}
                  loadingText={t('approving')}
                  leftIcon={<FiShield />}
                  isDisabled={!amount || parseInt(amount) < minAmount || isApproved || !!inviterAddressError || !!amountError}
                >
                  {isApproved ? t('approved') : t('approve')}
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* 预览与奖励模块 */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">{t('preview.title')}</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6}>
                {/* 当前DOR余额 - 固定显示 */}
                <VStack spacing={4} w="full">
                  <Text fontWeight="bold" fontSize="lg" color="blue.500">
                    当前DOR余额
                  </Text>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                    <Stat>
                      <StatLabel>流通DOR</StatLabel>
                      <StatNumber color="green.500" fontSize="lg">
                        {userCirculatingBalance ? parseFloat(userCirculatingBalance).toFixed(2) : '0.00'}
                      </StatNumber>
                      <StatHelpText>
                        <StatArrow type="increase" />
                        可自由交易的DOR
                      </StatHelpText>
                    </Stat>

                    <Stat>
                      <StatLabel>锁定DOR</StatLabel>
                      <StatNumber color="orange.500" fontSize="lg">
                        {userLockedAmount.toFixed(2)}
                      </StatNumber>
                      <StatHelpText>
                        <StatArrow type="increase" />
                        锁定中的DOR
                      </StatHelpText>
                    </Stat>
                  </Grid>
                </VStack>

                {/* 充值预览 - 仅在输入金额时显示 */}
                {previewData && !previewData.isCurrentBalance && (
                  <>
                    <Divider />
                    <VStack spacing={4} w="full">
                      <Text fontWeight="bold" fontSize="lg" color="green.500">
                        充值后预期余额
                      </Text>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                        <Stat>
                          <StatLabel>流通DOR</StatLabel>
                          <StatNumber color="green.500" fontSize="lg">
                            {previewData.circulatingDOR.toFixed(2)}
                          </StatNumber>
                          <StatHelpText>
                            <StatArrow type="increase" />
                            充值后流通DOR (+{previewData.newCirculatingDOR.toFixed(2)})
                          </StatHelpText>
                        </Stat>

                        <Stat>
                          <StatLabel>锁定DOR</StatLabel>
                          <StatNumber color="orange.500" fontSize="lg">
                            {previewData.lockedDOR.toFixed(2)}
                          </StatNumber>
                          <StatHelpText>
                            <StatArrow type="increase" />
                            充值后锁定DOR (+{previewData.newLockedDOR.toFixed(2)})
                          </StatHelpText>
                        </Stat>
                      </Grid>
                    </VStack>
                  </>
                )}

                {/* 套利提示 - 仅在输入金额时显示 */}
                {previewData && !previewData.isCurrentBalance && currentPrice > 1 && (
                  <Alert status="success" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>{t('arbitrage.opportunity')}</AlertTitle>
                      <AlertDescription>
                        {t('arbitrage.description')} +{((currentPrice - 1) * 100).toFixed(1)}% {t('arbitrage.profit')}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {/* 提示信息 - 当没有输入金额时显示 */}
                {(!previewData || previewData.isCurrentBalance) && (
                  <Box textAlign="center" py={4}>
                    <Box mb={2}><FiInfo size={24} color="var(--chakra-colors-gray-400)" /></Box>
                    <Text color="gray.500" fontSize="sm">输入USDT金额查看充值预览</Text>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        </Grid>

        {/* 风险与确认模块 */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardHeader>
            <Heading size="md">{t('risk.title')}</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* 风险列表 */}
              <Accordion allowToggle>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <FiAlertTriangle color="orange.500" />
                        <Text fontWeight="bold">{t('risk.importantRisk')}</Text>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <List spacing={3}>
                      <ListItem>
                        <FiCheckCircle color="red.500" />
                        {t('risk.lockedDORRisk')}
                      </ListItem>
                      <ListItem>
                        <FiCheckCircle color="red.500" />
                        {t('risk.priceRisk')}{currentPrice} USDT
                        {currentPrice < 0.99 && (
                          <Badge colorScheme="red" ml={2} size="sm">
                            {t('risk.belowPeg')}
                          </Badge>
                        )}
                        {currentPrice > 1.01 && (
                          <Badge colorScheme="green" ml={2} size="sm">
                            {t('risk.abovePeg')}
                          </Badge>
                        )}
                      </ListItem>
                      <ListItem>
                        <FiCheckCircle color="red.500" />
                        {t('risk.systemLimit')}
                      </ListItem>
                      <ListItem>
                        <FiCheckCircle color="red.500" />
                        {t('risk.reserveUsage')}
                      </ListItem>
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>

              {/* 风险确认复选框 */}
              <Checkbox
                isChecked={riskAccepted}
                onChange={(e) => setRiskAccepted(e.target.checked)}
                colorScheme="blue"
              >
                {t('risk.confirmRisk')}
              </Checkbox>

              {/* 确认按钮 */}
              <Button
                colorScheme="blue"
                size="lg"
                w="full"
                onClick={handleRecharge}
                isLoading={isLoading && currentStep === 2}
                loadingText={t('exchanging')}
                leftIcon={<FiDollarSign />}
                rightIcon={<FiArrowRight />}
                isDisabled={!isApproved || !riskAccepted || !previewData || !!inviterAddressError || !!amountError}
              >
                {t('exchange')}
              </Button>

              {/* 替代选项 */}
              <VStack spacing={3} w="full">
                <Button 
                  variant="link" 
                  colorScheme="blue" 
                  size="sm"
                  w="full"
                  onClick={() => window.open('https://testnet.pancakeswap.finance/swap', '_blank')}
                >
                  {t('alternatives.buyFromMarket')}
                </Button>
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* 我的邀请码 */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4}>
              <HStack justify="space-between" w="full">
                <Text fontWeight="bold">{t('inviteCode.myInviteCode')}</Text>
              </HStack>
              <VStack w="full" spacing={3}>
                <Input
                  value="DOR123456"
                  isReadOnly
                  size="lg"
                />
                <Button
                  size="lg"
                  w="full"
                  onClick={copyInviteCode}
                  leftIcon={copied ? <FiCheck /> : <FiCopy />}
                >
                  {copied ? t('inviteCode.copied') : t('inviteCode.copy')}
                </Button>
              </VStack>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                {t('inviteCode.shareDescription')}
              </Text>
            </VStack>
          </CardBody>
        </Card>

        {/* 风险免责横幅 */}
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>{t('risk.disclaimer')}</AlertTitle>
            <AlertDescription>
              {t('risk.disclaimerDescription')}
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>

      {/* 确认模态 */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>{t('modal.confirmMessage')} {amount} USDT {t('modal.exchangeFor')} {(parseFloat(amount) * 2).toFixed(2)} DOR？</Text>
              
              {previewData && (
                <Card variant="outline">
                  <CardBody>
                    <VStack spacing={2} align="stretch">
                      <HStack justify="space-between">
                        <Text>{t('modal.circulatingDOR')}</Text>
                        <Text fontWeight="bold" color="green.500">
                          +{previewData.newCirculatingDOR.toFixed(2)}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>{t('modal.lockedDOR')}</Text>
                        <Text fontWeight="bold" color="orange.500">
                          +{previewData.newLockedDOR.toFixed(2)}
                        </Text>
                      </HStack>
                      <Divider />
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('modal.total')}</Text>
                        <Text fontWeight="bold">
                          +{previewData.newDOR.toFixed(2)} DOR
                        </Text>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              )}

                             {/* 价格状态警告 */}
               {currentPrice < 0.99 && (
                 <Alert status={currentPrice < 0.95 ? "error" : "warning"}>
                   <AlertIcon />
                   <Box>
                     <AlertTitle>
                       {currentPrice < 0.95 ? t('modal.severePriceRisk') : t('modal.priceRiskReminder')}
                     </AlertTitle>
                     <AlertDescription>
                       {currentPrice < 0.95 
                         ? t('modal.severePriceRiskDesc')
                         : t('modal.priceRiskDesc')
                       }
                     </AlertDescription>
                   </Box>
                 </Alert>
               )}

               {currentPrice > 1.01 && (
                 <Alert status="success">
                   <AlertIcon />
                   <Box>
                     <AlertTitle>{t('modal.arbitrageOpportunity')}</AlertTitle>
                     <AlertDescription>
                       {t('modal.arbitrageDesc')}
                     </AlertDescription>
                   </Box>
                 </Alert>
               )}

               <Alert status="info">
                 <AlertIcon />
                 <Box>
                   <AlertTitle>{t('modal.gasReminder')}</AlertTitle>
                   <AlertDescription>
                     {t('modal.gasDesc')}
                   </AlertDescription>
                 </Box>
               </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('modal.cancel')}
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={executeRecharge}
              isLoading={isLoading}
              loadingText={t('modal.executing')}
            >
              {t('modal.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Recharge; 